import React from 'react';
import { cn } from '@/lib/utils';

interface AmbientBackdropProps {
  imageUrl?: string | null;
  className?: string;
  /** CSS blur amount in pixels */
  blur?: number;
  /** Scale factor for the backdrop background */
  scale?: number;
  /** Opacity for the backdrop layer (0-1) */
  opacity?: number;
}

/**
 * AmbientBackdrop
 * Renders a soft blurred and scaled background using the given image, with a subtle
 * radial gradient vignette and a low-opacity noise texture to add depth.
 *
 * Usage: place inside a relative container and before your foreground media.
 */
const AmbientBackdrop: React.FC<AmbientBackdropProps> = ({
  imageUrl,
  className,
  blur = 40,
  scale = 1.15,
  opacity = 0.9,
}) => {
  if (!imageUrl) return null;

  // Tiny SVG noise pattern as data URI
  const noiseSvg = `url("data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'>
      <filter id='n'>
        <feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch' />
        <feColorMatrix type='saturate' values='0' />
      </filter>
      <rect width='100%' height='100%' filter='url(%23n)' opacity='0.035'/>
    </svg>`
  )}")`;

  return (
    <div className={cn('absolute inset-0 overflow-hidden rounded-inherit', className)} aria-hidden>
      {/* Base blurred image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: `blur(${blur}px) saturate(120%)`,
          transform: `scale(${scale})`,
          opacity,
        }}
      />
      {/* Vignette / radial gradient to focus center */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(80% 70% at 50% 40%, rgba(255,255,255,0.08) 0%, rgba(0,0,0,0.55) 100%)',
          mixBlendMode: 'overlay',
        }}
      />
      {/* Soft top-to-bottom gradient for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-transparent to-black/40" />
      {/* Subtle noise */}
      <div className="absolute inset-0" style={{ backgroundImage: noiseSvg, backgroundRepeat: 'repeat' }} />
    </div>
  );
};

export default AmbientBackdrop;
